/**
 * LookupOverlay — dictionary entry shown when the player taps a filled
 * word in the ladder. Fetches a rich (multi-sense + examples) entry from
 * Wiktionary on mount; cached after first load.
 */
import { useEffect, useState } from 'react';
import type { DefinitionEntry, Locale } from './types';
import { fetchDefinition } from './dictionary';

interface Props {
  readonly word: string;
  readonly locale: Locale;
  readonly onClose: () => void;
}

export function LookupOverlay(props: Props) {
  const { word, locale, onClose } = props;
  const [entry, setEntry] = useState<DefinitionEntry | null | 'loading'>('loading');

  useEffect(() => {
    let cancelled = false;
    setEntry('loading');
    (async () => {
      const def = await fetchDefinition(locale, word);
      if (!cancelled) setEntry(def);
    })();
    return () => { cancelled = true; };
  }, [word, locale]);

  const source = locale === 'en' ? 'en.wiktionary.org' : `${locale}.wiktionary.org`;

  return (
    <div id="lookupOverlay" className="overlay">
      <div className="overlayScrim" onClick={onClose}></div>
      <div className="overlayCard lookup" role="dialog" aria-label="Dictionary">
        <header>
          <div className="lookupHead">
            <div className="lookupWord">{word}</div>
            <div className="lookupPos">
              {entry && entry !== 'loading' ? (entry.partOfSpeech ?? '') : ''}
            </div>
          </div>
          <button className="iconBtn ghost" aria-label="Close" onClick={onClose}>×</button>
        </header>

        <div className="lookupBody">
          {entry === 'loading' && (
            <div className="lookupLoading">Looking up…</div>
          )}
          {entry === null && (
            <div className="lookupEmpty">No dictionary entry found for this word.</div>
          )}
          {entry && entry !== 'loading' && entry.senses.map((s, i) => (
            <div key={i} className="lookupSense">
              <span className="num">{i + 1}.</span>
              <span className="gloss">{s.gloss}</span>
              {s.examples.length > 0 && (
                <ul className="examples">
                  {s.examples.map((ex, j) => <li key={j}>{ex}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>

        {entry && entry !== 'loading' && (
          <div className="lookupSource">via {source}</div>
        )}
      </div>
    </div>
  );
}
